/**
 * Secrets list page component.
 * Supports listing, creating/updating, and deleting secrets.
 *
 * @module pages/SecretsPage
 */

import { useState } from 'react';
import { useParams } from 'react-router';
import { KeyRound, Trash2, Plus, Search, Pencil } from 'lucide-react';
import { LoadingPage, ConfirmModal } from '@/components/ui';
import { useSecrets, useUpsertSecret, useDeleteSecret } from '@/hooks';
import {
  useSandboxGroupSecrets,
  useUpsertSandboxGroupSecret,
  useDeleteSandboxGroupSecret,
} from '@/hooks/useSandboxGroups';
import type { SecretResponse } from '@/types';

/**
 * Secrets list page
 */
export function SecretsPage() {
  const { groupId } = useParams<{ groupId?: string }>();

  const { data: userSecrets, isLoading: userLoading } = useSecrets(!groupId);
  const { data: groupSecrets, isLoading: groupLoading } = useSandboxGroupSecrets(
    groupId ?? '',
    !!groupId
  );
  const secrets = groupId ? groupSecrets : userSecrets;
  const isLoading = groupId ? groupLoading : userLoading;

  const userUpsertMutation = useUpsertSecret();
  const groupUpsertMutation = useUpsertSandboxGroupSecret(groupId ?? '');
  const userDeleteMutation = useDeleteSecret();
  const groupDeleteMutation = useDeleteSandboxGroupSecret(groupId ?? '');

  const upsertMutation = groupId ? groupUpsertMutation : userUpsertMutation;
  const deleteMutation = groupId ? groupDeleteMutation : userDeleteMutation;

  const [deleteTarget, setDeleteTarget] = useState<SecretResponse | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showUpsertModal, setShowUpsertModal] = useState(false);
  const [editTarget, setEditTarget] = useState<SecretResponse | null>(null);
  const [upsertId, setUpsertId] = useState('');
  const [upsertValues, setUpsertValues] = useState<{ key: string; value: string }[]>([
    { key: '', value: '' },
  ]);
  const [upsertError, setUpsertError] = useState('');

  // Filter secrets based on search query
  const filteredSecrets = secrets?.filter((secret) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return secret.id.toLowerCase().includes(query);
  });

  const handleDelete = () => {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget.id, {
        onSuccess: () => setDeleteTarget(null),
      });
    }
  };

  const openCreateModal = () => {
    setEditTarget(null);
    setUpsertId('');
    setUpsertValues([{ key: '', value: '' }]);
    setUpsertError('');
    setShowUpsertModal(true);
  };

  const openEditModal = (secret: SecretResponse) => {
    setEditTarget(secret);
    setUpsertId(secret.id);
    setUpsertValues([{ key: '', value: '' }]);
    setUpsertError('');
    setShowUpsertModal(true);
  };

  const addKeyValuePair = () => {
    setUpsertValues([...upsertValues, { key: '', value: '' }]);
  };

  const removeKeyValuePair = (index: number) => {
    setUpsertValues(upsertValues.filter((_, i) => i !== index));
  };

  const updateKeyValuePair = (index: number, field: 'key' | 'value', val: string) => {
    const updated = [...upsertValues];
    updated[index] = { ...updated[index], [field]: val };
    setUpsertValues(updated);
  };

  const handleUpsert = () => {
    setUpsertError('');

    const secretId = upsertId.trim();
    if (!secretId) {
      setUpsertError('Secret ID is required.');
      return;
    }

    // Build values dict, skip empty keys
    const values: Record<string, string> = {};
    for (const { key, value } of upsertValues) {
      const k = key.trim();
      if (k) {
        values[k] = value;
      }
    }

    if (Object.keys(values).length === 0) {
      setUpsertError('At least one key-value pair is required.');
      return;
    }

    upsertMutation.mutate(
      { secretId, values },
      {
        onSuccess: () => {
          setShowUpsertModal(false);
          setUpsertId('');
          setUpsertValues([{ key: '', value: '' }]);
          setEditTarget(null);
        },
        onError: (error) => {
          setUpsertError(error instanceof Error ? error.message : 'Failed to save secret.');
        },
      }
    );
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-3">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Secrets</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>
              <span className="hidden sm:inline">Create </span>Secret
            </span>
          </button>
        </div>
      </div>

      {/* Controls bar */}
      <div className="flex items-center gap-3 mb-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-400" />
          <input
            type="text"
            placeholder="Search by ID"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search secrets by ID"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-[#2a2a2a] dark:bg-[#161616] dark:text-white rounded-md focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg overflow-hidden flex flex-col">
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <table className="w-full" aria-label="Secrets">
            <thead className="bg-white dark:bg-[#0a0a0a] border-b border-gray-200 dark:border-[#2a2a2a]">
              <tr>
                <th
                  scope="col"
                  className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  ID
                </th>
                <th
                  scope="col"
                  className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Created
                </th>
                <th
                  scope="col"
                  className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Updated
                </th>
                <th
                  scope="col"
                  className="px-4 py-1.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-[#0a0a0a]">
              {!filteredSecrets || filteredSecrets.length === 0 ? (
                <tr className="bg-white dark:bg-[#0a0a0a]">
                  <td colSpan={4} className="px-4 py-16">
                    <div className="flex flex-col items-center justify-center text-center">
                      <KeyRound className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" />
                      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-1">
                        {searchQuery ? 'No matching secrets found.' : 'No Secrets yet.'}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                        {searchQuery
                          ? 'Try adjusting your search query.'
                          : 'Secrets store key-value pairs for use in egress policies.'}
                      </p>
                      {!searchQuery && (
                        <button
                          onClick={openCreateModal}
                          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 dark:bg-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <Plus className="h-4 w-4" />
                          Create Secret
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredSecrets.map((secret) => (
                  <tr
                    key={secret.id}
                    className="border-b border-gray-200 dark:border-[#2a2a2a] hover:bg-gray-50 dark:hover:bg-[#161616]"
                  >
                    <td className="px-4 py-1.5 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <KeyRound className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-200">
                          {secret.id}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {new Date(secret.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-1.5 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {new Date(secret.updatedAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-1.5 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openEditModal(secret)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] rounded-sm transition-colors"
                        >
                          <Pencil className="h-4 w-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteTarget(secret)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-sm transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Delete Secret"
        message={
          deleteTarget
            ? `Are you sure you want to delete secret "${deleteTarget.id}"? The secret will be soft-deleted and can be recreated with the same name.`
            : ''
        }
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />

      {/* Upsert modal */}
      {showUpsertModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-[#161616] rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-[#2a2a2a]">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editTarget ? 'Update Secret' : 'Create Secret'}
              </h2>
            </div>
            <div className="px-6 py-4 flex-1 overflow-y-auto space-y-4">
              {/* Secret ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Secret ID
                </label>
                <input
                  type="text"
                  value={upsertId}
                  onChange={(e) => setUpsertId(e.target.value)}
                  disabled={!!editTarget}
                  placeholder="e.g. github-token"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-[#2a2a2a] dark:bg-[#0a0a0a] dark:text-white rounded-md focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-60"
                />
              </div>

              {/* Key-value pairs */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Key-Value Pairs
                  </label>
                  <button
                    onClick={addKeyValuePair}
                    className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    + Add pair
                  </button>
                </div>
                <div className="space-y-2">
                  {upsertValues.map((pair, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={pair.key}
                        onChange={(e) => updateKeyValuePair(index, 'key', e.target.value)}
                        placeholder="Key"
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-[#2a2a2a] dark:bg-[#0a0a0a] dark:text-white rounded-md focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <input
                        type="password"
                        value={pair.value}
                        onChange={(e) => updateKeyValuePair(index, 'value', e.target.value)}
                        placeholder="Value"
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-[#2a2a2a] dark:bg-[#0a0a0a] dark:text-white rounded-md focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      {upsertValues.length > 1 && (
                        <button
                          onClick={() => removeKeyValuePair(index)}
                          className="p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Error */}
              {upsertError && (
                <p className="text-sm text-red-600 dark:text-red-400">{upsertError}</p>
              )}

              {editTarget && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Note: Updating a secret replaces all existing key-value pairs.
                </p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-[#2a2a2a] flex justify-end gap-3">
              <button
                onClick={() => setShowUpsertModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpsert}
                disabled={upsertMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-60"
              >
                {upsertMutation.isPending ? 'Saving...' : editTarget ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
